/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import { types, applySnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import * as constants from '../../../common/constants';
import utils from '../../../common/utils';

/**
 * Mobx State Tree Store
 */

const colorThemeObj = types.model('colorThemeObj', {
  themeID: types.maybeNull(types.integer),
  themeValue: types.maybeNull(types.string),
  uniqueID: types.maybeNull(types.string)
});

const colorInfoObj = types.model('colorInfoObj', {
  bookThemes: types.optional(types.array(colorThemeObj), [])
});

const languageInfoObj = types.model('languageInfoObj', {
  audioPlayerOnereader: types.maybeNull(types.string),
  hasMultipleAudioFlow: types.maybeNull(types.string),
  hasNonSynchAudioText: types.maybeNull(types.string),
  isUnderLineHotspot: types.maybeNull(types.string),
  pdzProductID: types.maybeNull(types.string),
  print: types.maybeNull(types.string),
  studyToolOnereader: types.maybeNull(types.string),
  serverSideEnabledOneReader: types.maybeNull(types.string),
  // PXE (Page Navigation and Print Settings)
  goToPageField: types.maybeNull(types.string),
  previousNextPageButtons: types.maybeNull(types.string),
  offlineAccess: types.maybeNull(types.string),
  ipadSwipeActions: types.maybeNull(types.string),
  includeMathMLLibraries: types.maybeNull(types.string),
  preventCopy: types.maybeNull(types.string),
  enableUserPrinting: types.maybeNull(types.string),
  printOption: types.maybeNull(types.string),
  audioBook: types.maybeNull(types.string)
});

// PXE nav menus from API: [{ label: string, isVisible: boolean }, ...]
const navMenuItemObj = types.model('navMenuItemObj', {
  label: types.maybeNull(types.string),
  isVisible: types.optional(types.boolean, false)
});

// PXE (Page Navigation and Print Settings) - matches API response shape
const pageNavigationPrintSettingObj = types.model('pageNavigationPrintSettingObj', {
  spineScrapper: types.optional(types.boolean, false),
  gotoPage: types.optional(types.boolean, false),
  enablePaging: types.optional(types.boolean, true),
  includeMathMLLib: types.optional(types.boolean, false),
  preventCopy: types.optional(types.boolean, false),
  enableIpadSwipe: types.optional(types.boolean, false),
  verticalScroll: types.optional(types.boolean, true),
  enablePrinting: types.optional(types.boolean, false),
  offlineAccess: types.optional(types.boolean, false),
  audioBook: types.maybeNull(types.string),
  // API returns [{ label, isVisible }, ...]; legacy string[] normalized in set()
  navMenus: types.optional(types.array(navMenuItemObj), [])
});

const InterfaceModel = types
  .model('InterfaceModel', {
    // interfaceState: types.optional(types.array(languageInfoObj), [])
    pageNavigationPrintSetting: types.optional(pageNavigationPrintSettingObj, {}),
    languageInfo: types.optional(languageInfoObj, {}),
    colorInfo: types.optional(colorInfoObj, {}),
    bookId: types.maybeNull(types.string),
    isPdzValidation: types.optional(types.boolean, false),
    /** PXE only: from API; PDZ Product ID editable when 1 or 10 */
    statusId: types.optional(types.maybeNull(types.integer), null)
  })
  .views((self) => ({
    getHotspotFeaturesConfig(bookId) {
      const hotspotObj = { ...constants.HOTSPOT_DEFAULT_FEATURE_CONFIG };
      if (self.bookId === bookId) {
        if (self.languageInfo) {
          hotspotObj.isUnderLineHotspot = utils.convertBoolean(self.languageInfo.isUnderLineHotspot);
        }
        if (self.pageNavigationPrintSetting) {
          hotspotObj.pageNavigationPrintSetting = self.pageNavigationPrintSetting;
        }
        if (self.colorInfo && self.colorInfo.bookThemes && self.colorInfo.bookThemes.length > 0) {
          const { bookThemes } = self.colorInfo;
          if (bookThemes.find((theme) => theme.themeID === 8)) {
            hotspotObj.hotSpotColor = bookThemes.find((theme) => theme.themeID === 8).themeValue;
          }
          if (bookThemes.find((theme) => theme.themeID === 14)) {
            hotspotObj.regionHotSpotAlpha = bookThemes.find((theme) => theme.themeID === 14).themeValue;
          }
          if (bookThemes.find((theme) => theme.themeID === 16)) {
            hotspotObj.underLineHotSpotColor = bookThemes.find((theme) => theme.themeID === 16).themeValue;
          }
          if (bookThemes.find((theme) => theme.themeID === 18)) {
            hotspotObj.underLineHotSpotThickness = bookThemes.find((theme) => theme.themeID === 18).themeValue;
          }
          if (bookThemes.find((theme) => theme.themeID === 17)) {
            hotspotObj.underLineHotSpotHoverColor = bookThemes.find((theme) => theme.themeID === 17).themeValue;
          }
        }
      }
      return hotspotObj;
    }
  }))
  .actions((self) => ({
    fetch(id) {
      Framework.getEventManager().publish(constants.COLOR_INTERFACE_DATA_FETCHED, { id });
    },

    set(response, event) {
      const payload = response?.data ?? response;
      const pxe = payload.pageNavigationPrintSetting;
      let pageNavigationPrintSetting = pxe;
      if (pxe && 'navMenus' in pxe) {
        const { navMenus, ...rest } = pxe;
        const normalizedNavMenus = (() => {
          if (navMenus == null) return [];
          if (typeof navMenus === 'string') {
            return [{ label: navMenus, isVisible: true }];
          }
          if (Array.isArray(navMenus)) {
            return navMenus.map((entry) =>
              typeof entry === 'string'
                ? { label: entry, isVisible: true }
                : { label: entry?.label ?? null, isVisible: entry?.isVisible ?? false }
            );
          }
          return [];
        })();
        pageNavigationPrintSetting = { ...rest, navMenus: normalizedNavMenus };
      }
      const model = {
        languageInfo: payload.languageInfo,
        pageNavigationPrintSetting,
        colorInfo: payload.colorInfo,
        bookId: event.id,
        isPdzValidation: payload.isPdzValidation,
        statusId: payload.statusId ?? null
      };
      applySnapshot(self, model);
    },

    savePayload(payload) {
      Framework.getEventManager().publish(constants.COLOR_INTERFACE_POST, { payload });
    }
  }));

export default InterfaceModel;
